import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { DrawingSet, DrawingRevision } from '@estimator/shared';

export function useDrawingSets(projectId: string, folderId: string | null) {
  return useQuery<{ data: DrawingSet[] }>({
    queryKey: ['drawing-sets', projectId, folderId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: DrawingSet[] }>(
        `/projects/${projectId}/drawing-sets`,
        { params: { folderId } },
      );
      return data;
    },
    enabled: !!projectId && !!folderId,
  });
}

export function useCreateDrawingSet(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; folder_id: string }) => {
      const { data: response } = await apiClient.post<DrawingSet>(
        `/projects/${projectId}/drawing-sets`,
        data,
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drawing-sets', projectId] });
    },
  });
}

export function useUpdateDrawingSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: { id: string; name?: string; sort_order?: number }) => {
      const { data: response } = await apiClient.patch<DrawingSet>(
        `/drawing-sets/${id}`,
        data,
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drawing-sets'] });
    },
  });
}

export function useDeleteDrawingSet(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/drawing-sets/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drawing-sets', projectId] });
    },
  });
}

export function useDeleteRevision(drawingSetId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/revisions/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revisions', drawingSetId] });
      queryClient.invalidateQueries({ queryKey: ['drawing-sets'] });
    },
  });
}

export function useRevisions(drawingSetId: string | null) {
  return useQuery<{ data: DrawingRevision[] }>({
    queryKey: ['revisions', drawingSetId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: DrawingRevision[] }>(
        `/drawing-sets/${drawingSetId}/revisions`,
      );
      return data;
    },
    enabled: !!drawingSetId,
  });
}

export function useUploadRevision(drawingSetId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { file: File; projectId: string }) => {
      const formData = new FormData();
      formData.append('files', data.file);
      formData.append('project_id', data.projectId);
      const { data: response } = await apiClient.post(
        `/drawing-sets/${drawingSetId}/revisions`,
        formData,
        { headers: { 'Content-Type': undefined } },
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revisions', drawingSetId] });
    },
  });
}

export function useSubmitRevision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (revisionId: string) => {
      const { data } = await apiClient.patch<DrawingRevision>(
        `/revisions/${revisionId}/submit`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revisions'] });
    },
  });
}

export function useApproveRevision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (revisionId: string) => {
      const { data } = await apiClient.patch<DrawingRevision>(
        `/revisions/${revisionId}/approve`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revisions'] });
    },
  });
}

export function useRequestRevise() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      revisionId,
      reviewNotes,
    }: {
      revisionId: string;
      reviewNotes: string;
    }) => {
      const { data } = await apiClient.patch<DrawingRevision>(
        `/revisions/${revisionId}/revise`,
        { review_notes: reviewNotes },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revisions'] });
    },
  });
}
